import { useCallback, useMemo, useState } from 'react'
import { Button, Skeleton, ToggleButton, ToggleButtonGroup } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, Pagination } from '@/components/ui/data-state'
import { PlusIcon } from '@/components/ui/icons'
import { errorMessage } from '@/lib/api/api-error'

import {
  useInventoryCategories,
  useInventoryLocations,
  useStockItems,
  useStockLevels,
} from '../hooks'
import { StockItemType } from '../types'
import type { StockItem, StockItemFilterValues, StockItemKind, StockItemQuery } from '../types'
import { locationLabel } from './inventory-format'
import { StockItemDetailDialog } from './stock-item-detail-dialog'
import { StockItemFilters } from './stock-item-filters'
import { StockItemFormDialog } from './stock-item-form-dialog'
import { StockItemsTable } from './stock-items-table'

/**
 * A page of stock items.
 *
 * At most 50, and kept there on purpose: picking a location reads that page's
 * stock in one batch call, and the batch takes 50 ids.
 */
const PAGE_SIZE = 25

const NO_FILTERS: StockItemFilterValues = { search: '', status: null, categoryId: null }

/** Each list's words, so the copy below names what is actually on screen. */
const KIND_COPY: Record<StockItemKind, { plural: string; empty: string; intro: string }> = {
  [StockItemType.Product]: {
    plural: 'products',
    empty: 'No products are counted as stock yet.',
    intro: 'The products you sell that are counted as stock',
  },
  [StockItemType.Ingredient]: {
    plural: 'ingredients',
    empty: 'No ingredients yet.',
    intro: 'What goes into what you sell — coffee beans, milk, syrup',
  },
}

interface StockItemsPanelProps {
  /**
   * Whose stock the rows show — `null` for every location's total. Held by the
   * page because the picker sits in the module bar, beside the tabs.
   */
  locationId: string | null
  /**
   * Which list shows — products or ingredients. Held by the page, which keeps it
   * in the URL, so a link or a reload opens the same list.
   */
  kind: StockItemKind
  onKindChange: (kind: StockItemKind) => void
  /**
   * Takes the merchant to an item's recipe on the Recipes tab. Held by the page,
   * because the tab is a route the panel does not know.
   */
  onShowRecipe: (item: StockItem) => void
}

/**
 * What the business counts, as two lists: the products it sells, and the
 * ingredients that go into them — with how much of each it holds. The figures
 * are totals across every location until one is picked; then they are that
 * location's, read for the visible page in one batch call.
 *
 * Two lists rather than one with a type column: a merchant looking for oat milk
 * and one checking how many bottled drinks are left are on different errands,
 * and each list is the whole of one. The switch sets the type the list is read
 * with, so paging and counts are each list's own.
 *
 * Only ingredients are created here, so "New ingredient" shows on that list
 * alone; any row on either list can be edited, in the same form. How much of
 * each is on hand is not typed in anywhere on this screen — receipts and stock
 * counts set it — so a new one starts at zero.
 *
 * Recipes are not edited here. An item's Recipe button, and the one in its
 * detail, lead to the Recipes tab with that item's recipe picked out.
 *
 * The list's own filters sit in the panel rather than the bar: they and a search
 * beside five tabs would wrap the bar onto a second line, which is the call the
 * Products tab made for its filters too.
 */
export function StockItemsPanel({
  locationId,
  kind,
  onKindChange,
  onShowRecipe,
}: StockItemsPanelProps) {
  const [filters, setFilters] = useState<StockItemFilterValues>(NO_FILTERS)
  const [page, setPage] = useState(0)
  /** The list the page was last on. */
  const [pagedFor, setPagedFor] = useState(kind)

  // Another list entirely: its own first page, under the same filters. Reset
  // while rendering rather than in the switch's handler, because the list can
  // also change from the URL — the back button, or a link.
  if (pagedFor !== kind) {
    setPagedFor(kind)
    setPage(0)
  }
  const [isFormOpen, setIsFormOpen] = useState(false)
  /** The row whose detail is open — an item made from a recipe, or an ingredient. */
  const [openItem, setOpenItem] = useState<StockItem | null>(null)
  /** The row being edited; `undefined` while the form is for a new ingredient. */
  const [editItem, setEditItem] = useState<StockItem | undefined>(undefined)
  /** Bumped on each opening, so the dialog mounts fresh rather than holding the last draft. */
  const [formSession, setFormSession] = useState(0)

  /** The one form, for a new ingredient or — given a row — a change to it. */
  function openForm(item?: StockItem) {
    setEditItem(item)
    setFormSession((session) => session + 1)
    setIsFormOpen(true)
  }

  /* A narrower list is a shorter one; the old offset may not exist in it, so the
     page resets in the same event. Stable, because the filters' debounce effect
     depends on it — a fresh function each render would restart the timer.

     A location change does not reset the page: it is the same list, with other
     figures in three of its columns. */
  const changeFilters = useCallback((next: StockItemFilterValues) => {
    setFilters(next)
    setPage(0)
  }, [])

  /* Each unset filter is left off the query. The service answers an empty value
     — `?status=` — with a 400, not with "no filter". `category` is the API's
     name for the item type. */
  const query = useMemo<StockItemQuery>(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      category: kind,
      status: filters.status ?? undefined,
      categoryId: filters.categoryId ?? undefined,
      search: filters.search.trim() || undefined,
    }),
    [kind, filters, page],
  )

  const { items, total, isLoading, isRefreshing, error, retry } = useStockItems(query)
  const { categories } = useInventoryCategories()
  const { locations } = useInventoryLocations()

  const itemIds = useMemo(() => items.map((item) => item.id), [items])
  const stock = useStockLevels(itemIds, locationId)

  const location = locations.find((candidate) => candidate.id === locationId) ?? null
  const isFiltered = Boolean(filters.search.trim() || filters.status || filters.categoryId)
  const isIngredients = kind === StockItemType.Ingredient
  const copy = KIND_COPY[kind]

  return (
    <>
      <ModuleIntro
        description={
          location
            ? `${copy.intro}, held at ${locationLabel(location)}. Archived ones are hidden unless you filter for them.`
            : `${copy.intro}, with totals across all your locations. Archived ones are hidden unless you filter for them.`
        }
        action={
          isIngredients && (
            <Button
              variant="primary"
              size="sm"
              className="h-9 rounded-full px-4 font-medium"
              onPress={() => openForm()}
            >
              <PlusIcon width={16} height={16} />
              New ingredient
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        {/* A segmented row, as on the employee form: two fixed lists are worth
            showing side by side, and the selected one reads as a raised pill. */}
        <ToggleButtonGroup
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([kind])}
          onSelectionChange={(keys) => {
            const [first] = keys as Set<string>
            if (first) onKindChange(first as StockItemKind)
          }}
          aria-label="Which items to list"
        >
          <ToggleButton
            id={StockItemType.Product}
            size="sm"
            className="data-[selected]:bg-background data-[selected]:text-foreground px-4 text-xs data-[selected]:shadow-sm"
          >
            Products
          </ToggleButton>
          <ToggleButton
            id={StockItemType.Ingredient}
            size="sm"
            className="data-[selected]:bg-background data-[selected]:text-foreground px-4 text-xs data-[selected]:shadow-sm"
          >
            Ingredients
          </ToggleButton>
        </ToggleButtonGroup>

        <StockItemFilters value={filters} onChange={changeFilters} categories={categories} />
      </div>

      {isLoading ? (
        <Skeleton className="h-80 w-full rounded-2xl" />
      ) : error ? (
        <ErrorState subject={`your ${copy.plural}`} error={error} onRetry={retry} />
      ) : items.length === 0 ? (
        <EmptyState
          title={isFiltered ? 'Nothing matches these filters.' : copy.empty}
          description={
            isFiltered
              ? 'Search matches any part of a name or SKU. Archived items only show when Status is set to Archived.'
              : isIngredients
                ? 'Add what goes into what you sell — coffee beans, milk, flour, syrup.'
                : 'A product shows here once it is set up to be counted as stock.'
          }
          action={
            isFiltered
              ? { label: 'Clear filters', onPress: () => changeFilters(NO_FILTERS) }
              : isIngredients
                ? { label: 'New ingredient', onPress: () => openForm() }
                : undefined
          }
        />
      ) : (
        <>
          {/* Inline, above a table that stays up: the items loaded, only one
              location's figures did not, and those rows already say so with
              dashes rather than numbers. */}
          {stock.error && (
            <div
              role="alert"
              className="border-danger/40 flex flex-wrap items-center gap-3 rounded-2xl border border-dashed px-4 py-3 text-sm"
            >
              <span className="flex-1">
                {errorMessage(stock.error, 'Could not load the stock at this location.')}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="border-border h-9 rounded-full border px-4"
                onPress={stock.retry}
              >
                Try again
              </Button>
            </div>
          )}

          <div
            className={
              isRefreshing || stock.isRefreshing ? 'opacity-60 transition-opacity' : undefined
            }
          >
            <StockItemsTable
              items={items}
              levels={locationId ? stock.levels : null}
              onOpen={setOpenItem}
              onEdit={openForm}
              onRecipe={onShowRecipe}
              showDefaultCost={isIngredients}
            />
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />

          <p className="text-muted text-xs">
            {location
              ? `On hand and value are what ${locationLabel(location)} holds. Out of stock is flagged here; low stock is judged on the totals, since a reorder level is set for the whole business.`
              : 'On hand and value are totals across every location. Status is judged on what is available — on hand less what open orders have reserved.'}
          </p>
        </>
      )}

      <StockItemDetailDialog
        item={openItem}
        onOpenChange={(open) => {
          if (!open) setOpenItem(null)
        }}
        onShowRecipe={onShowRecipe}
      />

      <StockItemFormDialog
        key={formSession}
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSaved={() => setIsFormOpen(false)}
        item={editItem}
      />
    </>
  )
}
